export interface AnalyticsDriver {
  supported(): Promise<boolean>;
  initialize(): void;
  ready(): Promise<unknown>;
  consent(enabled: boolean): void;
  collection(enabled: boolean): void;
  event(name: string, params: Record<string, unknown>): void;
}

// One controller per document. A revision is invalidated by every choice/route
// transition, including grant -> deny -> grant while asynchronous work is pending.
export function createAnalyticsLifecycle(driver: AnalyticsDriver, permitted: () => boolean) {
  let revision = 0;
  let active = false;
  let initialized = false;
  let ready = false;
  let failed = false;
  let starting: Promise<void> | undefined;
  let enabled = false;
  const allowed = () => active && !failed && permitted();
  const collecting = () => enabled && allowed();

  function apply() {
    enabled = initialized && allowed();
    if (initialized) {
      driver.collection(enabled);
      driver.consent(allowed());
    }
  }
  function sync() {
    revision++;
    apply();
    if (!allowed() || starting || initialized) return;
    starting = (async () => {
      try {
        if (!(await driver.supported())) { failed = true; return; }
        if (!allowed()) return;
        // initialize is synchronous; all later SDK/config/script work remains gated.
        driver.consent(true);
        driver.initialize();
        initialized = true;
        apply();
        await driver.ready();
        ready = true;
        apply();
      } catch {
        failed = true;
        enabled = false;
        if (initialized) { driver.collection(false); driver.consent(false); }
      }
    })().finally(() => {
      starting = undefined;
      // A grant can arrive between a canceled support check and this finalizer.
      if (allowed() && !initialized) sync();
    });
  }
  return {
    collecting,
    permitted: allowed,
    currentRevision: () => revision,
    accepts: (token: unknown) => ready && collecting() && token === revision,
    sync,
    start() { active = true; sync(); },
    stop() { active = false; sync(); },
    async event(name: string, params: Record<string, unknown>) {
      if (!allowed()) return;
      const token = revision;
      if (!starting && !initialized) sync();
      const eventRevision = starting && token !== revision ? revision : token;
      await starting;
      if (!ready || !collecting() || eventRevision !== revision) return;
      driver.event(name, { ...params, _untangledRevision: eventRevision });
    },
  };
}
